import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier, parseProductResponseBody } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

// Fanka:变体切换耗时几乎全在网络(TTFB 0.4~0.9s,客户端解析+替换仅 ~50ms)。
// 所以做两件事:缓存 section 响应(来回切 0 网络)+ 悬停/按下时提前发请求。

/** @type {Map<string, { ts: number, promise: Promise<string> }>} */
const SECTION_CACHE = new Map();

const SECTION_CACHE_TTL = 60_000; // 库存价格会变,缓存只在当前这段浏览里有效
const SECTION_CACHE_MAX = 40;

const PREFETCH_HOVER_DELAY = 80; // 鼠标横扫一排色块时别每格都发请求

/** 下游监听器统一按 AbortError 判断,这里保持一致 */
function createAbortError() {
  return new DOMException('Fetch aborted by user', 'AbortError');
}

/**
 * 取变体 section 的 HTML 文本,命中未过期缓存则直接复用。
 * signal 只用于「结果回来时是否已被新选择取代」的判断,不中断网络 —— 预取的响应留在缓存里下次就是 0ms。
 */
function fetchSectionHtml(url, { signal, priority, cache = true } = {}) {
  if (!cache) {
    return fetch(url, { signal }).then((response) => response.text());
  }

  const hit = SECTION_CACHE.get(url);
  const promise =
    hit && Date.now() - hit.ts < SECTION_CACHE_TTL
      ? hit.promise
      : (() => {
          const pending = fetch(url, priority ? { priority } : undefined)
            .then((response) => {
              if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
              return response.text();
            })
            .catch((error) => {
              // 失败不能留在缓存里,否则这个组合在 TTL 内一直报错
              SECTION_CACHE.delete(url);
              throw error;
            });

          SECTION_CACHE.set(url, { ts: Date.now(), promise: pending });

          // Map 迭代顺序即插入顺序,超量丢最旧
          if (SECTION_CACHE.size > SECTION_CACHE_MAX) {
            const oldest = SECTION_CACHE.keys().next().value;
            if (oldest !== undefined) SECTION_CACHE.delete(oldest);
          }

          return pending;
        })();

  return promise.then((text) => {
    if (signal?.aborted) throw createAbortError();
    return text;
  });
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #prefetchTimer;

  /** 直接读取当前 fieldset，避免依赖异步更新的组件 refs。 */
  #getFieldsets() {
    return /** @type {HTMLFieldSetElement[]} */ (Array.from(this.querySelectorAll('fieldset[ref="fieldsets[]"]')));
  }

  /**
   * 变体 section morph 后重建尺码/选项的运行时状态。
   *
   * morph 会复用已有节点并保留滑动动画使用的 data-current-checked，
   * 但换色时尺码列表可能增删或重排，旧节点上的标记就不再代表当前 HTML。
   * 以 morph 同步后的真实 checked 属性为准，避免 CSS 把旧颜色的选中态带到
   * 新颜色的可售/缺货按钮上，同时让后续点击使用新的 radio 索引。
   * @param {(string | undefined)[][]} previousOptionIds
   * @returns {HTMLInputElement | undefined} 需要回退选择的第一个可售选项
   */
  #syncVariantPickerState(previousOptionIds = []) {
    const fieldsets = this.#getFieldsets();
    const previousIndices = this.#checkedIndices;
    let fallbackOption;

    this.#radios = [];
    this.#checkedIndices = [];

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = Array.from(
        /** @type {NodeListOf<HTMLInputElement>} */ (fieldset.querySelectorAll('input[type="radio"]'))
      );
      const checkedIndex = radios.findIndex((radio) => radio.checked);
      const firstAvailableIndex = radios.findIndex((radio) => radio.dataset.optionAvailable === 'true');
      const previousIds = previousOptionIds[fieldsetIndex];
      const indices = previousIndices[fieldsetIndex];
      // 列表和当前选择都没变时保留正在进行的滑动动画；增删/重排时清掉旧轨迹。
      const preserveHistory =
        previousIds?.length === radios.length &&
        radios.every((radio, index) => radio.dataset.optionValueId === previousIds[index]) &&
        checkedIndex !== -1 &&
        indices?.[0] === checkedIndex;

      this.#radios.push(radios);
      this.#checkedIndices.push(preserveHistory ? indices : checkedIndex === -1 ? [] : [checkedIndex]);

      if (checkedIndex === -1 && firstAvailableIndex !== -1 && !fallbackOption) {
        fallbackOption = radios[firstAvailableIndex];
      }

      if (!preserveHistory) {
        fieldset.style.removeProperty('--pill-width-current');
        fieldset.style.removeProperty('--pill-width-previous');
      }

      radios.forEach((radio, index) => {
        radio.dataset.currentChecked = radio.checked ? 'true' : 'false';
        radio.dataset.previousChecked = preserveHistory && index === indices[1] ? 'true' : 'false';
      });
    });

    return fallbackOption;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#syncVariantPickerState();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    // 悬停(PC)/ 按下 / 聚焦时提前发请求
    this.addEventListener('pointerover', this.#handlePrefetchHover, { passive: true });
    this.addEventListener('pointerdown', this.#handlePrefetchIntent, { passive: true });
    this.addEventListener('focusin', this.#handlePrefetchIntent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    clearTimeout(this.#prefetchTimer);
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    // 同商品变体只请求当前商品信息 section；跨商品组合仍需请求完整 main。
    const sameProductSectionId = !loadsNewProduct && isOnProductPage ? this.dataset.sectionId : null;
    const optionValueId = selectedOption.dataset.optionValueId ?? '';
    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption, null, [], sameProductSectionId),
      morphElementSelector,
      optionValueId,
      event.deferredEventPromise
    );

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = this.#getFieldsets();
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = this.#getFieldsets();
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {string | null} [sectionIdOverride] - 商品页同商品变体请求使用的 section ID。
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], sectionIdOverride = null) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'quick-add-dialog': this.dataset.sectionId,
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    const sectionId = closestSectionId ? SECTION_ID_MAP[closestSectionId] : sectionIdOverride;

    return this.#composeRequestUrl(productUrl, params, sectionId);
  }

  /**
   * 按固定顺序拼请求地址:section_id → view → option_values。
   * 真实请求和预取必须共用它,否则地址差一个字符缓存键就对不上。
   * - params:已拼好的查询串片段,如 ['view=xxx', 'option_values=1,2']
   * @param {string | undefined} productUrl
   * @param {string[]} params
   * @param {string | undefined | null} sectionId
   * @returns {string}
   */
  #composeRequestUrl(productUrl, params, sectionId) {
    if (sectionId) {
      const base = productUrl?.includes('?') ? productUrl.split('?')[0] : productUrl;
      return `${base}?section_id=${sectionId}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * 是不是「商品页上切同一个商品变体」的 picker —— 只有这种情况预取地址才和真实点击一致。
   * @returns {boolean}
   */
  #isSameProductSectionPicker() {
    if (this.dataset.templateProductMatch !== 'true') return false;

    // 这几个容器在 buildRequestUrl 里各有自己的 SECTION_ID_MAP 映射,地址形态不同
    const scopedContainers = [
      'product-card',
      'quick-add-component',
      'quick-add-dialog',
      'swatches-variant-picker-component',
      'featured-product-information',
    ];

    return !scopedContainers.some((selector) => this.closest(selector));
  }

  /**
   * 找出值得预取的候选 radio;不是选项控件 / 已选中 / 组合商品都返回 null。
   * @param {EventTarget | null} target
   * @returns {HTMLInputElement | null}
   */
  #prefetchCandidate(target) {
    if (!(target instanceof Element)) return null;

    const input =
      target.closest('label')?.querySelector('input[type="radio"]') ?? target.closest('input[type="radio"]');

    if (!(input instanceof HTMLInputElement)) return null;
    if (input.checked) return null; // 已是当前值,点了也不发请求
    if (input.dataset.connectedProductUrl) return null; // 组合商品要整页 morph,预取几 MB 不划算

    return input;
  }

  /**
   * 鼠标悬停预取,防抖后再发,否则横扫一排色块会把每个组合都请求一遍。
   * @param {PointerEvent} event
   */
  #handlePrefetchHover = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return; // 触屏没有悬停,交给 pointerdown

    const candidate = this.#prefetchCandidate(event.target);
    if (!candidate) return;

    clearTimeout(this.#prefetchTimer);
    this.#prefetchTimer = setTimeout(() => this.#prefetchOption(candidate), PREFETCH_HOVER_DELAY);
  };

  /**
   * 手指按下 / 键盘聚焦时立即预取;pointerdown 比 click 早 100~300ms,移动端就靠这段提前量。
   * @param {Event} event
   */
  #handlePrefetchIntent = (event) => {
    const candidate = this.#prefetchCandidate(event.target);
    if (candidate) this.#prefetchOption(candidate);
  };

  /**
   * 发出预取请求:只写缓存不碰 DOM,失败静默(用户真点时会照常走正常流程)。
   * @param {HTMLInputElement} candidate
   */
  #prefetchOption(candidate) {
    const url = this.#buildPrefetchUrl(candidate);
    if (!url) return;

    fetchSectionHtml(url, { priority: 'low' }).catch(() => {});
  }

  /**
   * 算出「假如选了这个值」会请求的地址:把同一组的已选值换成候选值,其余保持文档顺序。
   * @param {HTMLInputElement} candidate
   * @returns {string | null}
   */
  #buildPrefetchUrl(candidate) {
    if (!this.#isSameProductSectionPicker()) return null;

    const sectionId = this.dataset.sectionId;
    const productUrl = this.dataset.productUrl;
    const optionValueId = candidate.dataset.optionValueId;

    if (!sectionId || !productUrl || !optionValueId) return null;

    // 和 selectedOptionsValues 用同一个选择器,保证顺序一致
    const currentOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    if (!currentOptions.length) return null;

    // 认 fieldset 而不是 data-option-name:fanka 自定义色块 snippet 没输出那个属性
    const candidateFieldset = candidate.closest('fieldset');

    let replaced = false;
    /** @type {string[]} */
    const optionValues = [];

    for (const option of currentOptions) {
      if (!(option instanceof HTMLElement)) return null;

      const isSameGroup = candidateFieldset
        ? option.closest('fieldset') === candidateFieldset
        : option.dataset.optionName === candidate.dataset.optionName;

      if (isSameGroup) {
        replaced = true;
        optionValues.push(optionValueId);
        continue;
      }

      const value = option.dataset.optionValueId;
      if (!value) return null; // 缺一项就拼不出合法地址

      optionValues.push(value);
    }

    if (!replaced) return null;

    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);
    params.push(`option_values=${optionValues.join(',')}`);

    return this.#composeRequestUrl(productUrl, params, sectionId);
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {string} [optionValueId] - The selected option value ID for event detail.
   * @param {{promise: Promise, resolve: Function, reject: Function}} [deferredEventPromise] - 回退尺码时复用首次请求的事件 promise。
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, optionValueId = '', deferredEventPromise) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const isNewProductSelectEvent = !deferredEventPromise;
    deferredEventPromise ||= ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    if (isNewProductSelectEvent) {
      this.dispatchEvent(
        new ProductSelectEvent({
          product: {
            id: this.dataset.productId ?? '',
            title: this.dataset.productTitle ?? '',
            handle: this.dataset.productHandle ?? '',
          },
          selectedOptions,
          detail: {
            optionValueId,
          },
          promise: deferredEventPromise.promise,
        })
      );
    }

    // 只有主商品页同商品请求走缓存;Quick Add/Featured/组合商品不共享这份缓存。
    const cacheable = this.#isSameProductSectionPicker() && requestUrl.includes('section_id=');

    fetchSectionHtml(requestUrl, { signal: this.#abortController.signal, cache: cacheable })
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = this.closest('quick-add-dialog')
          ? parseProductResponseBody(responseText)
          : new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        // 胸围尺码映射也包含 JSON，按语义标记读取实际变体，避免误把映射数据发布给购买表单。
        const variantPickerJsonScript = html.querySelector('variant-picker script[data-selected-variant]');
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          const updateResult = this.updateVariantPicker(html);
          newProduct = updateResult.newProduct;

          // 当前尺码在新颜色中不存在时，先选第一个可售尺码，再沿用正常变体请求链刷新商品信息。
          if (updateResult.fallbackOption) {
            this.variantChanged({ target: updateResult.fallbackOption, deferredEventPromise });
            return;
          }

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | DocumentFragment | Element} newHtml - The new HTML.
   * @returns {{newProduct: NewProduct | undefined, fallbackOption: HTMLInputElement | undefined}}
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // 必须在 morph 改写原节点前保存选项身份，不能用旧节点引用反查。
    const previousOptionIds = this.#radios.map((radios) => radios.map((radio) => radio.dataset.optionValueId));
    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    const fallbackOption = this.#syncVariantPickerState(previousOptionIds);
    this.updateVariantPickerCss();

    return { newProduct, fallbackOption };
  }

  updateVariantPickerCss() {
    const fieldsets = this.#getFieldsets();

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
